import Alerter from 'cogo-toast';
import Color from 'color';
import React, { ElementType } from 'react';
import DefaultMessage from '../components/DefaultMessage';
import { TEMPLATE_PAGE_SEPARATOR, TOAST_TIMEOUT } from '../constants';

export const numberWithCommas = (num: number) => {
  const [whole, decimal] = num.toString().split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimal || '00'}`;
};

export const formatToCurrency = (amount: number) =>
  `₦${numberWithCommas(amount)}`;

export const getRelativePeriod = (then = 0) => {
  if (!then || !Number(then)) {
    return '';
  }
  const period = Date.now() - new Date(then).getTime();
  if (period < 0) {
    return '';
  }
  const periodInSeconds = Math.round(period / 1000);
  if (periodInSeconds <= 5) {
    return 'moments ago';
  }
  if (periodInSeconds < 60) {
    return `${periodInSeconds} seconds ago`;
  }
  const periodInMinutes = Math.round(periodInSeconds / 60);
  if (periodInMinutes < 60) {
    return `${periodInMinutes} minutes ago`;
  }
  const periodInHours = Math.round(periodInMinutes / 60);
  if (periodInHours < 24) {
    return `${periodInHours} hours ago`;
  }
  const periodInDays = Math.round(periodInHours / 24);
  if (periodInDays < 30) {
    return `${periodInDays} days ago`;
  }
  const periodInMonths = Math.round(periodInDays / 30);
  if (periodInMonths < 12) {
    return `${periodInMonths} months ago`;
  }
  const periodInYears = Math.round(periodInMonths / 12);
  return `${periodInYears} years ago`;
};

export const urlsMatch = (url: string, path: string, exact = false) =>
  !!url.match(new RegExp(`^${path}${exact ? '$' : ''}`));

export class Alert {
  static alert(
    type: 'success' | 'error' | 'info' | 'loading' | 'warn',
    message: string,
    options: {
      header?: string;
      actionBtnText?: string;
      MessageComponent?: ElementType;
      callback?: () => void;
    } = {}
  ): { hide?: () => void; [key: string]: any } {
    const alert = Alerter[type];
    const {
      header,
      actionBtnText,
      MessageComponent = DefaultMessage,
      callback
    } = options;
    if (alert) {
      const { hide, ...rest } = alert(
        // @ts-ignore
        <MessageComponent
          header={header}
          message={message}
          actionBtnText={actionBtnText}
          callback={callback}
        />,
        {
          position: 'top-right',
          hideAfter: TOAST_TIMEOUT / 1000,
          onClick: () => {
            hide();
          },
          ...options
        }
      );
      return {
        hide,
        ...rest
      };
    }
    return {};
  }

  static info(message: string, options = {}) {
    return Alert.alert('info', message, options);
  }

  static success(message: string, options = {}) {
    return Alert.alert('success', message, options);
  }

  static error(message: string, options = {}) {
    return Alert.alert('error', message, options);
  }
}

export const cleanURLCompoment = (str: string): string => {
  if (str) {
    str = decodeURIComponent((str + '').replace(/\+/g, '%20'));
    return str;
  } else {
    return '';
  }
};

export const isValidColor = (str: string): boolean => {
  if (!str) {
    return false;
  }
  try {
    Color(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const isHexColorCode = (str: string): boolean => {
  if (!str) {
    return false;
  }
  return !!str.match(/^#([0-9a-f]{3}){1,2}$/i);
};

export const parseQueryString = (
  queryString: string,
  extraData = {}
): [string, { [key: string]: any }] => {
  if (!queryString) {
    return ['', extraData];
  }
  const [path, search] = queryString.split('?');
  if (!search) {
    return [path, extraData];
  }
  const state: { [attr: string]: any } = {};
  search.split('&').forEach(serializedParam => {
    const [key, value] = serializedParam.split('=');
    if (value === 'true') {
      state[key] = true;
    } else if (value === 'false') {
      state[key] = false;
    } else {
      state[key] = value;
    }
  });
  return [path, { ...state, ...extraData }];
};

export const concatPaths = (...paths: string[]) =>
  paths.reduce((acc, curr, index) => {
    if (curr === '/') {
      return acc;
    }
    let path = curr;
    path = path.startsWith('/') ? path.slice(1) : path;
    if (index < paths.length - 1) {
      path += path.endsWith('/') ? '' : '/';
    } else {
      path = path.endsWith('/') ? path.slice(0, path.length - 1) : path;
    }
    return `${acc}${path}`;
  }, '/');

export const appendPageIdToUrl = (...args: string[]) => {
  const nextArgs = args.reduce<any[]>((acc, curr, index) => {
    if (index === args.length - 1) {
      return [...acc, curr];
    }
    return [...acc, curr, TEMPLATE_PAGE_SEPARATOR];
  }, []);
  return concatPaths(...nextArgs);
};

export const appendPageDataToPageId = (pageId: string, obj?: object) => {
  if (!obj) {
    return pageId;
  }
  const nextObj: { [attr: string]: any } = {
    ...obj,
    routeId: undefined,
    pageId: undefined,
    modal: undefined,
    fullScreen: undefined
  };
  const keys = Object.keys(nextObj);
  let query = keys.reduce((acc, key) => {
    if (nextObj[key] === null || nextObj[key] === undefined) {
      return acc;
    }
    return `${acc}${key}=${nextObj[key]}&`;
  }, '#');
  query =
    query[query.length - 1] === '&' || query[query.length - 1] === '#'
      ? query.slice(0, query.length - 1)
      : query;
  return pageId + query;
};

export const keyMatch = <T extends any>(
  obj: { [attr: string]: T },
  key: string
): T | null => {
  if (key in obj) {
    return obj[key];
  }
  const keys = Object.keys(obj);
  const match = keys.find(currKey => currKey.match(new RegExp(`${key}`)));
  if (!match) {
    return null;
  }
  return obj[match];
};

export const appendValidObject = (
  key: string,
  value: any
): { [attr: string]: any } => {
  if (value) {
    return { [key]: value };
  }
  return {};
};

export const refreshPage = () => {
  const [place] = window.location.href.split(`/${TEMPLATE_PAGE_SEPARATOR}/`);
  window.location.assign(place);
};

export type Transaction = {
  asw_memo: string;
  asw_value: number;
  asw_date: number | string;
  asw_id: number | string;
};

export type FormattedTransaction = {
  type: 'credit' | 'debit';
  description: string;
  amount: number;
  createdAt: number;
  id: number | string;
};

export const formatTransactions = (
  raw: Transaction[]
): FormattedTransaction[] =>
  raw.map(
    ({
      asw_value: amount,
      asw_memo: description,
      asw_date: createdAt,
      asw_id: id
    }) => ({
      type: amount > 0 ? 'credit' : 'debit',
      description,
      amount,
      createdAt: Number(createdAt),
      id
    })
  );

export const replaceString = (
  str: string,
  data: { [field: string]: string } = {}
) => {
  let result = str;
  Object.keys(data).forEach(key => {
    const value = data[key];
    result = result.replace(`%${key}%`, value);
  });
  return result;
};

export const selectAllText = (node: HTMLElement) => {
  if (window.getSelection) {
    const selection = window.getSelection() as Selection;
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

const removeAllNonNumericCharacters = (str: string) =>
  str.replace(/[^\d]/g, '');

export const getNumberNotationLength = (length: number) => {
  if (!length) {
    return 0;
  }
  const x = Math.floor((length - 1) / 3);
  return x + length;
};

export const numberNormalizer = (rawValue: string): string => {
  if (!rawValue) {
    return '0';
  }
  const numbersCommaAndDotOnly = rawValue.replace(/[^0-9,.]/g, '');
  if (!numbersCommaAndDotOnly) {
    return '0';
  }
  let decimalIndex: number = -1;
  for (let i = 0; i < numbersCommaAndDotOnly.length; i += 1) {
    let char = numbersCommaAndDotOnly[i];
    if (char === '.') {
      decimalIndex = i;
      break;
    }
  }
  const rawWholePart = numbersCommaAndDotOnly.slice(
    0,
    decimalIndex === -1 ? numbersCommaAndDotOnly.length : decimalIndex
  );
  let onlyNums = removeAllNonNumericCharacters(rawWholePart);
  while (onlyNums[0] === '0') {
    onlyNums = onlyNums.slice(1);
  }
  const wholePartArr = Array.from({
    length: getNumberNotationLength(onlyNums.length)
  });
  const noOfCommas = wholePartArr.length - onlyNums.length;
  let offset = 0;
  for (let i = onlyNums.length - 1; i >= 0; i = i - 1) {
    if (onlyNums.length - 1 - i && (onlyNums.length - 1 - i) % 3 === 0) {
      wholePartArr[i - offset + noOfCommas] = ',';
      offset += 1;
    }
    wholePartArr[i - offset + noOfCommas] = onlyNums[i];
  }
  const wholePart = wholePartArr.join('') || '0';

  if (decimalIndex === -1) {
    return wholePart;
  }
  const rawDecimalPart = numbersCommaAndDotOnly.slice(decimalIndex + 1);
  const decimalPart = removeAllNonNumericCharacters(rawDecimalPart);
  return wholePart + '.' + decimalPart;
};

export const isURL = (urlTest: string) => {
  return urlTest.startsWith('https:') || urlTest.startsWith('http:');
};
